<template>
  <div>
    <ErrorLayout
      v-if="productData?.error"
      :error="productData?.error"
    />
    <div v-else>
      <div
        v-if="!isRedirecting"
        class="product flex flex-col items-center"
        itemscope
        :itemtype="SCHEMA_ITEM_TYPE.PRODUCT"
      >
        <MissingTranslationNotification
          class="product__missing-translation-notification hidden my-2 px-4 lg:block"
        />
        <div class="product__main-wrapper px-4 max-w-full flex flex-col relative">
          <div
            v-if="productData?.shouldShowSimilarProducts"
            class="relative lg:hidden"
          >
            <SimilarProducts
              class="wide-background py-2"
              :similar-products="productData?.similarProducts"
              :similar-products-loading="similarProductsLoading"
              :source-sku="product.sku"
              :has-arrows="false"
              :size="PRODUCT_CARD_SIZE.TILE_MINI"
            />
          </div>
          <ProductBreadcrumbs
            v-if="productData?.product"
            :product="productData.product"
            class="h-12 lg:self-start w-full"
          />
          <div
            v-else
            class="h-12 lg:self-start w-full flex items-center pl-4"
          >
            <SkeletonLoader
              height="100%"
              class="h-5 w-3/4 lg:w-2/5"
            />
          </div>
          <ProductDetailsSection
            v-if="productData?.product && productData?.attributes"
            class="product__main-container"
            :product="productData?.product"
            :attributes-localized="productData?.attributes"
            :product-adjusted-for-cart="productData?.productAdjusted"
            :available-quantity="availableQuantity"
            :stock="stock"
            :loading-stocks="loadingStocks"
            :top-review="topReview"
            :should-show-related-accessories="!!(productData?.shouldShowRelatedAccessories)"
            :has-in-stock-alternative-products="productData?.hasInStockAlternativeProducts"
          />
          <ProductDetailsSectionSkeleton v-else />
          <ComplementaryProducts
            v-if="productData?.canShowComplementaryProducts"
            class="mt-5"
            :complementary-products="productData?.complementaryProducts"
          />
          <ProductAnchorSections
            :product="productData?.product"
            :attributes-localized="productData?.attributes"
            :related-accessories="productData?.relatedAccessoriesList"
            :related-accessories-loading="relatedAccessoriesLoading"
            :parents-for-accessory="productData?.parentsForAccessory"
            :parents-for-accessory-loading="parentsForAccessoryLoading"
            :reviews="reviews"
            :should-show-related-accessories="!!(productData?.shouldShowRelatedAccessories)"
            :alternative-products-data="productData?.alternativeProductsData"
            :in-stock-alternative-products-data="productData?.inStockAlternativeProductsData"
            :similar-products="productData?.similarProducts"
            :similar-products-loading="similarProductsLoading"
          />
        </div>
        <TrustedBy />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useLazyAsyncData } from '#app';
import { sendRedirect } from 'h3';
import ErrorLayout from '~/layouts/error.vue';
import { productFilterForAddingToCart } from '~~/helpers/filters/productFilters';
import { PRODUCT_CARD_SIZE } from '~~/constants/productCardSize';
import { getProductSku } from '~~/helpers/routes/getPageInformation';
import ProductBreadcrumbs from '~/components/molecules/Product/ProductBreadcrumbs.vue';
import { SCHEMA_ITEM_TYPE } from '~~/constants/seo';
import { STATUS_RESOURCE_NOT_FOUND } from '~~/constants/http';
import { getProductPerCategoryFilter } from '~~/helpers/commercetools/graphql/getProductPerCategoryFilter';
import { getMostRelevantCategory } from '~~/helpers/product/getMostRelevantCategory';
import { excludeSkuFilter } from '~~/helpers/commercetools/graphql/excludeSkuFilter';
import { CUSTOM_QUERIES } from '~~/constants/customQueries';
import { PRODUCT_ATTRIBUTES, SET_PRODUCT_SKU_BEGINNING_CHARS } from '~~/constants/products';
import { getProductAttributesExtended } from '~~/helpers/product/getProductAttributesExtended';
import createAccessoriesProductSearchParams from '~~/helpers/product/createAccessoriesProductSearchParams';
import { isSecondHandProduct } from '~~/helpers/product/getProductInfo/getProductInfo';
import { productGetters, reviewGetters } from '~/utils/ct';
import { getProductIsOnStock } from '~~/helpers/product/getProductStockInfo/getProductStockInfo';
import { generateMetaTitleWithCTFormat } from '~~/helpers/commercetools/meta/generateMetaTitle';
import { getMetaDescription, isPdpPageIndexed } from '~~/helpers/product/getMetaInfo/getMetaInfo';
import { getLocalizedSlugsForHreflangs } from '~~/helpers/product/getLocalizedSlugsForHreflangs';
import { Logger } from '~~/helpers/logger';

const props = defineProps<{
  slug: string;
}>();
const { slug } = toRefs(props);
const sku = computed(() => getProductSku(slug.value));
const error = ref<{ statusCode: number } | null>(null);
const event = useRequestEvent();
const i18n = useI18n();
const { event: ciaEvent } = useCia();
const { trackViewProduct } = useGtmEvents();
const { bloomreachCookie, addBloomreachCookieChangeListener } = useCookies();
const { seoRedirection } = useSeoRedirection();
const { callPageViewEvent } = usePageViewEvent();
const { addProductToLastSeen } = useLastSeenProducts();
const {
  search: searchProduct,
  loading: productLoading,
  masterProduct: product,
  masterProductLocalisedAttributes: attributesLocalized,
} = useProductExtended(slug.value);
const {
  products: similarProducts,
  search: similarProductsSearch,
  loading: similarProductsLoading,
} = useProductExtended(slug.value + '-similarProducts');
const {
  productsInStock: relatedAccessories,
  search: searchRelatedAccessories,
  loading: relatedAccessoriesLoading,
} = useProductRelated(`${slug.value}-relatedAccessories`);
const {
  productsInStock: parentsForAccessory,
  search: searchParentsForAccessory,
  loading: parentsForAccessoryLoading,
} = useProductRelated(`${slug.value}-parentForAccessory`);
const {
  productsData: alternativeProductsData,
  search: searchAlternativeProducts,
} = useAlternativeProducts(slug.value);
const {
  search: searchComplementaryProducts,
  complementaryProducts,
} = useComplementaryProductsStore();
const {
  availableQuantity,
  isQuantitySelectionPossible,
  stock,
} = useAddToCart(slug.value, productGetters.getSku(product.value));
const { addTags } = useCache();
const { ciaState } = useCiaState();
const {
  getStocks,
  loading: loadingStocks,
} = useStock(slug.value);
const isProductReady = computed(() => !!product.value && !productLoading.value);
const isReadyToSendViewItemEvent = computed(() =>
  isProductReady.value && !!bloomreachCookie.value,
);
const productId = computed(() => productGetters.getId(product.value));
const {
  reviews: productReviews,
  search: searchReviews,
} = useReviewExtended(productId);
const {
  redirectIfProductSlugMismatch,
  redirectIfNotPublishedOrNotEligibleForSelling,
  isRedirecting,
} = useProductRedirection();
const { data: productData } = await useLazyAsyncData(`pdp-${sku.value}`, async () => {

  error.value = null;
  isRedirecting.value = false;

  try {

    const pdpSearchParams = {
      skus: [sku.value],
    };

    if (!product.value || product.value.sku !== sku.value) {

      await searchProduct(pdpSearchParams, { includeUnsellableProducts: true });

    }

    let productSlugRedirect = null;

    if (import.meta.server) {

      productSlugRedirect = redirectIfProductSlugMismatch(slug.value, product.value);

    }

    if (productSlugRedirect) {

      if (event) {

        return sendRedirect(event, productSlugRedirect.to, productSlugRedirect.redirectCode);

      }

      return navigateTo(productSlugRedirect.to, {
        redirectCode: productSlugRedirect.redirectCode,
      });

    }

    if (product.value && !isRedirecting.value) {

      let productAvailabilityRedirect = null;

      if (import.meta.server) {

        productAvailabilityRedirect = redirectIfNotPublishedOrNotEligibleForSelling(product);

      }

      if (productAvailabilityRedirect) {

        if (event) {

          return sendRedirect(event, productAvailabilityRedirect.to, productAvailabilityRedirect.redirectCode);

        }

        return navigateTo(productAvailabilityRedirect.to, {
          redirectCode: productAvailabilityRedirect.redirectCode,
        });

      }
      const searchPromises = [];
      searchPromises.push(getStocks({ sku: product.value.sku }));
      searchPromises.push(searchReviews());
      searchPromises.push(searchComplementaryProducts(product.value.sku));
      searchPromises.push(
        similarProductsSearch(
          {
            customFilters: `
                ${getProductPerCategoryFilter(getMostRelevantCategory(product.value))}
                ${excludeSkuFilter(product.value.sku)}`,
          },
          {
            customQuery: CUSTOM_QUERIES.PRODUCT_SEARCH.perCategoryQuery,
            isOnStock: true,
          },
        ),
      );
      const isSetOfProducts = !!product.value?.sku?.startsWith(SET_PRODUCT_SKU_BEGINNING_CHARS);

      if (!isSetOfProducts) {

        searchPromises.push(searchAlternativeProducts(product.value));

      }
      const accessoriesList = getProductAttributesExtended(
        product.value,
        {
          attributeNames: [PRODUCT_ATTRIBUTES.RELATED_ACCESSORIES],
        },
      )[PRODUCT_ATTRIBUTES.RELATED_ACCESSORIES];

      if (accessoriesList) {

        const accessoriesParams = createAccessoriesProductSearchParams(accessoriesList);
        searchPromises.push(searchRelatedAccessories(accessoriesParams));

      }
      const parentProductsForAccessory = getProductAttributesExtended(
        product.value,
        {
          attributeNames: [PRODUCT_ATTRIBUTES.PARENT_PRODUCTS_FOR_ACCESSORY],
        },
      )[PRODUCT_ATTRIBUTES.PARENT_PRODUCTS_FOR_ACCESSORY];

      if (parentProductsForAccessory) {

        const parentProductsForAccessoryParams =
          createAccessoriesProductSearchParams(parentProductsForAccessory);
        searchPromises.push(searchParentsForAccessory(parentProductsForAccessoryParams));

      }
      await Promise.allSettled(searchPromises);
      addTags([
        { prefix: CacheTagPrefix.View, value: 'pdp' },
        { prefix: CacheTagPrefix.Product, value: slug.value },
      ]);

    } else {

      let productAvailabilityRedirect = null;

      if (import.meta.server) {

        productAvailabilityRedirect = redirectIfNotPublishedOrNotEligibleForSelling(product);

      }

      if (productAvailabilityRedirect) {

        if (event) {

          return sendRedirect(event, productAvailabilityRedirect.to, productAvailabilityRedirect.redirectCode);

        }

        return navigateTo(productAvailabilityRedirect.to, {
          redirectCode: productAvailabilityRedirect.redirectCode,
        });

      }
      // await is necessary here else we get error(Cannot set headers after they are sent to client)
      const seoRedirect = await seoRedirection();

      if (seoRedirect) {

        if (event) {

          return sendRedirect(event, seoRedirect.to, seoRedirect.redirectCode);

        }

        return navigateTo(seoRedirect.to, {
          redirectCode: seoRedirect.redirectCode,
          external: seoRedirect.external,
        });

      }
      console.log(`404 statuscode for slug : ${slug.value}`);

      const statusCode = STATUS_RESOURCE_NOT_FOUND;
      error.value = { statusCode };


      if (event) {

        setResponseStatus(event, STATUS_RESOURCE_NOT_FOUND);

      }

    }

  } catch (e) {

    console.error('error during searchProduct', e);

  }

  const relatedAccessoriesList = productGetters.getFiltered(relatedAccessories.value || [], { master: true });

  const inStockAlternativeProductsData = alternativeProductsData.value.filter(
    (alternativeProduct) => getProductIsOnStock(alternativeProduct.product),
  ) || [];

  return {
    product: product.value || null,
    attributes: attributesLocalized.value || null,
    productAdjusted:  product.value ? productFilterForAddingToCart(product.value) : null,
    similarProducts: similarProducts.value || [],
    shouldShowSimilarProducts: product.value &&
      product.value.sku &&
      similarProducts.value &&
      similarProducts.value.length,
    complementaryProducts: complementaryProducts.value || [],
    canShowComplementaryProducts: product.value && product.value.sku && complementaryProducts.value?.length,
    shouldShowRelatedAccessories: relatedAccessoriesList &&
      relatedAccessoriesList.length &&
      !isSecondHandProduct(product.value),
    relatedAccessoriesList,
    alternativeProductsData: alternativeProductsData.value || [],
    inStockAlternativeProductsData,
    hasInStockAlternativeProducts: inStockAlternativeProductsData?.length > 0,
    parentsForAccessory: productGetters.getFiltered(parentsForAccessory.value, { master: true }) || [],
    error: error.value,
  };

});

const reviews = computed(() => reviewGetters.getItems(productReviews.value));
const topReview = computed(() => reviews.value.find(review => review.text));

const defaultMetaDescription =
    i18n.t('SEO meta description PDP', { productTitle: productGetters.getName(product.value) });
useMetaExtended({
  metaTitle: computed(() => generateMetaTitleWithCTFormat({
    title: productGetters.getName(product.value),
    i18n,
    statusCode: error.value?.statusCode,
  })),
  isPageIndexed: computed(() => isPdpPageIndexed(product.value)),
  metaDescription: computed(() => getMetaDescription(product.value) || defaultMetaDescription),
  slug,
  localisedSlugs: computed(() => product.value ? getLocalizedSlugsForHreflangs(product.value) : []),
  isPdp: true,
  imagePath: computed(() => product.value ? productGetters.getCoverImage(product.value) : undefined),
});

if (import.meta.client) {

  addBloomreachCookieChangeListener();
  watch(() => (isReadyToSendViewItemEvent.value), async (readytoSendViewItemEvent) => {

    if (readytoSendViewItemEvent) {

      try {

        await ciaEvent.viewItem({
          sku: product.value.sku,
          email: ciaState.value.email,
          userId: ciaState.value.userId,
        });

      } catch (error) {

        Logger.error(`cia|viewItem error: ${error}`);

      }

    }

  }, { immediate: true });

}
watch(() => (isProductReady.value), (isReady) => {

  if (isReady) {

    trackViewProduct({
      product: product.value,
      inStock: isQuantitySelectionPossible.value,
    });

    addProductToLastSeen(product.value);

  }

}, { immediate: true });

onMounted(async () => {

  // Wait for DOM updates (including document.title) and pass explicit title to prevent timing issues
  await nextTick();
  const productName = productGetters.getName(product.value);
  callPageViewEvent({ pageTitle: productName });

});

</script>


<style lang="scss" scoped>
.product {
  &__missing-translation-notification {
    max-width: var(--container-max-width);
  }

  &__main {
    &-wrapper {
      --price-regular-font-size: 32px;
      --price-regular-line-height: 40px;
      --heading-horizontal-align: left;
      --heading-title-color: var(--_c-dark-primary);
      --heading-title-font-size: var(--pdp-h1-font-size);
      --heading-title-font-line-height: var(--pdp-h1-line-height);

      width: var(--container-max-width);
    }
  }

  &__drag-icon {
    animation: moveicon 1s ease-in-out infinite;
  }

  &__rating {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin: var(--spacer-xs) 0 var(--spacer-xs);
  }

  &__count {
    @include font(
      --count-font,
      var(--font-weight--normal),
      var(--font-size--sm),
      1.4,
      var(--font-family--secondary)
    );

    color: var(--c-text);
    text-decoration: none;
    margin: 0 0 0 var(--spacer-xs);
  }

  &__description {
    @include font(
      --product-description-font,
      var(--font-weight--light),
      var(--font-size--base),
      1.6,
      var(--font-family--primary)
    );
  }

  &__select-size {
    margin: 0 var(--spacer-sm);

    @include for-desktop {
      margin: 0;
    }
  }

  &__colors {
    @include font(
      --product-color-font,
      var(--font-weight--normal),
      var(--font-size--lg),
      1.6,
      var(--font-family--secondary)
    );

    display: flex;
    align-items: center;
    margin-top: var(--spacer-xl);
  }

  &__color-label {
    margin: 0 var(--spacer-lg) 0 0;
  }

  &__color {
    margin: 0 var(--spacer-2xs);
  }

  &__guide,
  &__compare,
  &__save {
    display: block;
    margin: var(--spacer-xl) 0 var(--spacer-base) auto;
  }

  &__compare {
    margin-top: 0;
  }

  &__tabs {
    margin: var(--spacer-lg) auto var(--spacer-2xl);

    --tabs-title-font-size: var(--font-size--lg);

    @include for-desktop {
      margin-top: var(--spacer-2xl);
    }
  }

  &__property {
    margin: var(--spacer-base) 0;

    &__button {
      --button-font-size: var(--font-size--base);
    }
  }

  &__review {
    @apply border-b;

    padding-bottom: 24px;
    margin-bottom: var(--spacer-base);
  }

  &__additional-info {
    @apply text-EXPONDO-grey;

    @include font(
      --additional-info-font,
      var(--font-weight--light),
      var(--font-size--sm),
      1.6,
      var(--font-family--primary)
    );

    &__title {
      font-weight: var(--font-weight--normal);
      font-size: var(--font-size--base);
      margin: 0 0 var(--spacer-sm);

      &:not(:first-child) {
        margin-top: 3.5rem;
      }
    }

    &__paragraph {
      margin: 0;
    }
  }

  &__gallery {
    flex: 1;
  }

  &-quick-links {
    margin-top: var(--spacer-m);
  }
}

.wide-background {
  &::before {
    content: "";
    left: calc(-50vw + 50%);
    z-index: -1;

    @apply absolute top-0 bottom-0 block w-screen h-full bg-EXPONDO-secondary-light;
  }
}

@keyframes moveicon {
  0% {
    transform: translate3d(0, 0, 0);
  }

  50% {
    transform: translate3d(0, 30%, 0);
  }

  100% {
    transform: translate3d(0, 0, 0);
  }
}
</style>
